import { Hidden } from '@ahmdigital/ui';
import { IconList } from '@ahmdigital/icons-web';
import { upperFirst } from 'lodash/fp';
import { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';

import {
  ErrorText,
  FieldWrapper,
  IconWrapper,
  InnerInputWrapper,
  InputWrapper,
  ModalWrapper,
  StepWrapper,
  StyledIcon,
} from './styles';
import { StepInputAppearance, WidgetButtonSpacing, WidgetData } from '../../types';
import analytics from '../../../../analytics';
import DisplayValue from '../display-value';
import getPanelIndexForError from '../../utils/get-panel-index-for-error';
import useEmbeddedWidgetContext from '../../hooks/use-embedded-widget-context';
import useStepInputFieldInteractions from './hooks/use-step-input-field-interactions';

export type StepInputProps = WidgetData & {
  buttonSpacing?: WidgetButtonSpacing;
  /** Position of this widget in the form sequence. Used to track which panel has errors and manage focus during validation. */
  index: number;
  /** Whether this is the first widget. Used for styling (top border, padding adjustments). */
  isFirst?: boolean;
  /** Whether this is the last widget. Last widget renders the submit button and should have different bottom styling. */
  isLast?: boolean;
  fluidWidth?: boolean;
  isModalOpen?: boolean;
  onModalClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  setIsModalOpen: (value: boolean) => void;
  stepInputAppearance?: StepInputAppearance;
  showFieldsRequiredLabel?: boolean;
};

// TODO: Potentially step input doesn't really describe what this is, within the embedded quote these are just the fields but they encapsulate all the logic, input fields, labels and modals. A better name might be WidgetField. Within the WidgetProvider we reference modalFields so maybe there's some overlap to renaming for consistency. Essentially these are form sections, but viewed in the widget they do feel like steps. Maybe FieldSection? Open to suggestions, but worth considering to improve clarity of the codebase.
const StepInput = <T,>({
  buttonSpacing,
  displayValue,
  fieldWidth,
  fluidWidth,
  iconName,
  index,
  inputComponent: InputComponent,
  isFirst,
  isLast,
  isModalOpen,
  modalContentComponent: ModalContentComponent,
  name,
  onModalClose,
  onMouseEnter,
  onMouseLeave,
  placeholder,
  setIsModalOpen,
  stepInputAppearance,
  showFieldsRequiredLabel = true,
}: StepInputProps) => {
  const asterisk = showFieldsRequiredLabel ? '*' : '';
  const sharedInputProps = {
    isLast,
    label: `${upperFirst(name)}${asterisk}`,
    name,
    placeholder,
  };
  const stepWrapperRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const autoCompleteRef = useRef<HTMLDivElement>(null);

  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { errors, isSubmitting, setFieldTouched, submitCount } = useFormikContext<T>();
  const errorText = errors[name as keyof T];
  const showErrorText = errorText && isModalOpen && hasError;

  // Default to chevron-down-light when no icon is provided. Chevrons animate (rotate to chevron up) on modal open.
  const defaultIconName: IconList = 'chevron-down-light';
  const resolvedIconName = iconName ?? defaultIconName;
  const isAnimate = isModalOpen && resolvedIconName === defaultIconName;
  const isOpen = isModalOpen || isSuggestionsOpen;
  const setIsModalContentOpen = (value: boolean) => {
    setIsModalOpen(value);
    if (!value) {
      onModalClose();
    }
  };

  const { modalFields } = useEmbeddedWidgetContext<T>();
  const firstErrorPanelIndex = getPanelIndexForError({ errors, modalFields });
  useEffect(() => {
    if (firstErrorPanelIndex === null) {
      setHasError(false);
      return;
    }
    const isCurrentPanel = firstErrorPanelIndex === index && Boolean(isModalOpen);
    if (submitCount > 0) {
      setHasError(isCurrentPanel);
      // Read focus at validation time so focus changes do not rerun this effect and steal focus back.
      if (isCurrentPanel && !stepWrapperRef.current?.contains(document.activeElement)) {
        sectionRef.current?.focus();
      }
    }
  }, [firstErrorPanelIndex, index, isSubmitting, isModalOpen, submitCount]);

  const handleWrapperClick = () => {
    autoCompleteRef.current?.focus();
    analytics.publish('action', {
      action: 'Clicked',
      component: 'Element',
      label: `Get a quote ${name}`,
    });
  };
  const { onBlur, onClick, onFocus, onMouseDown, onPointerDown } = useStepInputFieldInteractions({
    isModalOpen,
    setIsModalOpen: setIsModalContentOpen,
    setIsSuggestionsOpen,
  });

  return (
    <StepWrapper $fieldWidth={fieldWidth} $fluidWidth={fluidWidth} $isLast={isLast} ref={stepWrapperRef}>
      <FieldWrapper
        $hasError={hasError}
        $isFirst={isFirst}
        $isLast={isLast}
        $isOpen={isOpen}
        onBlur={onBlur}
        onClick={(event) => {
          onClick(event);
          handleWrapperClick();
        }}
        onFocus={onFocus}
        onMouseDown={onMouseDown}
        onPointerDown={onPointerDown}
        tabIndex={0}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={sectionRef}
      >
        <InputWrapper
          $appearance={stepInputAppearance}
          $buttonSpacing={buttonSpacing}
          $hasError={hasError}
          $isOpen={isOpen}
          $isLast={isLast}
        >
          <InnerInputWrapper>
            {InputComponent ? (
              <InputComponent
                setIsModalOpen={setIsModalOpen}
                modalContentRef={modalContentRef}
                onModalClose={onModalClose}
                setIsSuggestionsOpen={setIsSuggestionsOpen}
                autoCompleteRef={autoCompleteRef}
                {...sharedInputProps}
              />
            ) : (
              displayValue && (
                <DisplayValue
                  label={displayValue.customLabel ?? name}
                  getValue={displayValue.getValue}
                  hasValue={displayValue.hasValue}
                  showFieldsRequiredLabel={showFieldsRequiredLabel}
                />
              )
            )}
            <IconWrapper $appearance={stepInputAppearance}>
              <StyledIcon name={resolvedIconName} $isAnimate={isAnimate} size="medium" />
            </IconWrapper>
          </InnerInputWrapper>
          <Hidden xs sm md>
            {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call. */}
            {showErrorText && <ErrorText>{errorText}</ErrorText>}
          </Hidden>
        </InputWrapper>
      </FieldWrapper>

      {isOpen && (
        <ModalWrapper $isFirst={isFirst} $isLast={isLast} $hasError={hasError} $isOpen={isOpen}>
          {isSuggestionsOpen ? (
            <div ref={modalContentRef} />
          ) : (
            <>
              <Hidden df lg>
                {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call. */}
                {showErrorText && <ErrorText>{errorText}</ErrorText>}
              </Hidden>
              <ModalContentComponent
                autoCompleteRef={autoCompleteRef}
                setIsModalOpen={setIsModalContentOpen}
                setFieldTouched={setFieldTouched}
              />
            </>
          )}
        </ModalWrapper>
      )}
    </StepWrapper>
  );
};

export default StepInput;
